import { useCallback, useEffect, useState } from "react";
import { FiHelpCircle } from "react-icons/fi";
import SupportThread from "../components/SupportThread";
import { useNotifications } from "../context/NotificationContext";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import { getMySupportChat, markSupportRead, sendSupportAttachment, sendSupportMessage, supportFileUrl } from "../api/support";

/**
 * Chat with Mintly support. The conversation starts with the person's first message;
 * admins answer from the admin panel, and replies arrive here live (and as a notification).
 *
 * @see mintly-back/src/services/supportChat
 */
const SupportPage = ({ isDark, setToast }) => {
  const { t } = useTranslation();
  const { socket } = useSocket();
  // Reading the chat reads its notice too; the bell hears it from here, not a minute later.
  const { fetchNotifications } = useNotifications();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  const add = useCallback((message) => {
    setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMySupportChat();
        if (cancelled) return;
        setConversation(res.data?.conversation || null);
        setMessages(res.data?.messages || []);
        setHasMore(!!res.data?.hasMore);
        fetchNotifications();
      } catch (error) {
        if (!cancelled) setToast?.(error?.response?.data?.message || t("support.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setToast, t, fetchNotifications]);

  // Replies read as they arrive while the page is in view; otherwise when it's back in view.
  const readIfVisible = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const res = await markSupportRead();
      if (res.data?.conversation) setConversation(res.data.conversation);
      fetchNotifications();
    } catch {
      // Read on the next visit instead.
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket) return;
    const onMessage = ({ conversation: next, message }) => {
      if (!message) return;
      add(message);
      if (next) setConversation(next);
      if (message.fromSupport) readIfVisible();
    };
    const onRead = ({ at }) => setConversation((c) => (c ? { ...c, seenAt: at } : c));
    const onStatus = ({ conversation: next }) => next && setConversation(next);
    socket.on("support_message", onMessage);
    socket.on("support_read", onRead);
    socket.on("support_status", onStatus);
    return () => {
      socket.off("support_message", onMessage);
      socket.off("support_read", onRead);
      socket.off("support_status", onStatus);
    };
  }, [socket, add, readIfVisible]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && conversation?.unread > 0) readIfVisible();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [conversation?.unread, readIfVisible]);

  const send = async (content) => {
    setSending(true);
    try {
      const res = await sendSupportMessage(content);
      if (res.data?.message) add(res.data.message);
      if (res.data?.conversation) setConversation(res.data.conversation);
      return true;
    } catch (error) {
      setToast?.(error?.response?.data?.message || t("support.sendError"));
      return false;
    } finally {
      setSending(false);
    }
  };

  /** A photo, video or file, with its caption; resolves true once sent. */
  const sendFile = async (file, caption, onProgress) => {
    try {
      const res = await sendSupportAttachment(file, caption, onProgress);
      if (res.data?.message) add(res.data.message);
      if (res.data?.conversation) setConversation(res.data.conversation);
      return true;
    } catch (error) {
      setToast?.(error?.response?.data?.message || t("chatFiles.sendError", { name: file.name }));
      return false;
    }
  };

  const loadEarlier = async () => {
    if (!messages.length) return;
    setLoadingEarlier(true);
    try {
      const res = await getMySupportChat(messages[0].createdAt);
      const earlier = res.data?.messages || [];
      setMessages((prev) => [...earlier.filter((m) => !prev.some((p) => p.id === m.id)), ...prev]);
      setHasMore(!!res.data?.hasMore);
    } catch (error) {
      setToast?.(error?.response?.data?.message || t("support.loadError"));
    } finally {
      setLoadingEarlier(false);
    }
  };

  const closed = conversation?.status === "closed";

  return (
    <div className="flex h-full w-full flex-col gap-4 pb-24 lg:pb-0">
      <div>
        <h1 className={`text-3xl font-bold ${baseText}`}>{t("support.title")}</h1>
        <p className={`mt-1 text-subheadline ${mutedText}`}>{t("support.subtitle")}</p>
      </div>

      <div
        className={`flex h-[calc(100vh-240px)] min-h-[360px] flex-col overflow-hidden rounded-radius-lg border shadow-xl backdrop-blur-sm lg:h-[min(760px,calc(100vh-200px))] ${cardBase}`}
      >
        <div className={`flex items-center gap-3 border-b px-5 py-4 border-separator-default`}>
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent-tinted text-accent-text">
            <FiHelpCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className={`font-bold ${baseText}`}>{t("support.team")}</div>
            <div className={`text-footnote ${mutedText}`}>{closed ? t("support.statusClosed") : t("support.statusOpen")}</div>
          </div>
        </div>

        {loading ? (
          <div className={`flex flex-1 items-center justify-center text-subheadline ${mutedText}`}>{t("common.loading")}</div>
        ) : (
          <SupportThread
            side="user"
            messages={messages}
            isDark={isDark}
            seenAt={conversation?.seenAt}
            hasMore={hasMore}
            loadingEarlier={loadingEarlier}
            onLoadEarlier={loadEarlier}
            onSend={send}
            onSendFile={sendFile}
            fileUrl={(m, options) => supportFileUrl(m.id, options)}
            onFileError={setToast}
            sending={sending}
            placeholder={t("support.placeholder")}
            emptyText={t("support.empty")}
            notice={closed ? t("support.closedNotice") : null}
            senderLabel={(m) => (m.fromSupport ? t("support.team") : null)}
          />
        )}
      </div>
    </div>
  );
};

export default SupportPage;
